import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where,
  Timestamp, FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions,
  orderBy, runTransaction
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';
import { useAccount } from './AccountContext';
import { Transaction, useTransaction } from './TransactionContext';

// Define types
export type PayrollEntry = {
  id?: string;
  employeeName: string;
  employeeId: string;
  payPeriodStart: Date;
  payPeriodEnd: Date;
  hoursWorked: number;
  hourlyRate: number;
  overtimeHours: number;
  overtimeRate: number;
  grossPay: number;
  deductions: number;
  netPay: number;
  paymentDate: Date;
  status: 'pending' | 'paid' | 'cancelled';
  accountId: string; // Added accountId field
  budgetId: string;
  createdBy?: string;
  transactionId?: string; // Added to track associated transaction
};

type PayrollState = {
  payrollEntries: PayrollEntry[];
  totalPayroll: number;
  monthlyPayroll: Record<string, number>;
  loading: boolean;
  error: string | null;
};

type PayrollAction =
  | { type: 'ADD_PAYROLL_ENTRY'; payload: PayrollEntry }
  | { type: 'LOAD_PAYROLL_ENTRIES'; payload: Partial<PayrollState> }
  | { type: 'UPDATE_PAYROLL_ENTRY'; payload: PayrollEntry }
  | { type: 'DELETE_PAYROLL_ENTRY'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

type PayrollContextType = {
  state: PayrollState;
  addPayrollEntry: (entry: Omit<PayrollEntry, 'id' | 'createdBy' | 'budgetId' | 'transactionId'>) => Promise<void>;
  loadPayrollEntries: () => Promise<void>;
  updatePayrollEntry: (entry: PayrollEntry, originalEntry: PayrollEntry) => Promise<void>;
  deletePayrollEntry: (entryId: string) => Promise<void>;
  clearError: () => void;
};

const initialState: PayrollState = {
  payrollEntries: [],
  totalPayroll: 0,
  monthlyPayroll: {},
  loading: false,
  error: null,
};

const PayrollContext = createContext<PayrollContextType | undefined>(undefined);

function payrollReducer(state: PayrollState, action: PayrollAction): PayrollState {
  switch (action.type) {
    case 'ADD_PAYROLL_ENTRY':
      return {
        ...state,
        payrollEntries: [...state.payrollEntries, action.payload],
        loading: false,
      };
    case 'LOAD_PAYROLL_ENTRIES':
      return {
        ...state,
        ...action.payload,
        loading: false,
      };
    case 'UPDATE_PAYROLL_ENTRY':
      return {
        ...state,
        payrollEntries: state.payrollEntries.map((entry) =>
          entry.id === action.payload.id ? action.payload : entry
        ),
        loading: false,
      };
    case 'DELETE_PAYROLL_ENTRY':
      return {
        ...state,
        payrollEntries: state.payrollEntries.filter((entry) => entry.id !== action.payload),
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

const payrollConverter: FirestoreDataConverter<PayrollEntry> = {
  toFirestore: (entry: PayrollEntry) => {
    return {
      ...entry,
      payPeriodStart: Timestamp.fromDate(entry.payPeriodStart),
      payPeriodEnd: Timestamp.fromDate(entry.payPeriodEnd),
      paymentDate: Timestamp.fromDate(entry.paymentDate),
    };
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): PayrollEntry => {
    const data = snapshot.data(options);
    return {
      ...data,
      id: snapshot.id,
      payPeriodStart: data.payPeriodStart.toDate(),
      payPeriodEnd: data.payPeriodEnd.toDate(),
      paymentDate: data.paymentDate.toDate(),
    } as PayrollEntry;
  },
};

export function PayrollProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(payrollReducer, initialState);
  const { state: budgetState } = useBudgetManager();
  const { state: accountState, updateAccount } = useAccount();
  const { addTransaction, deleteTransaction } = useTransaction();

  // Helper function to update account balance
  const updateAccountBalance = async (
    accountId: string,
    amount: number,
    operation: 'deduct' | 'add'
  ) => {
    const account = accountState.accounts.find((a) => a.id === accountId);
    if (!account) throw new Error('Account not found');

    const newBalance = operation === 'deduct' 
      ? account.balance - amount
      : account.balance + amount;

    await updateAccount({
      ...account,
      balance: newBalance,
    });

    return newBalance;
  };

  const createPayrollTransaction = async (entry: PayrollEntry): Promise<string> => {
    const transaction: Omit<Transaction, 'id'> = {
      description: `Payroll payment for ${entry.employeeName}`,
      amount: entry.netPay,
      date: entry.paymentDate,
      category: 'payroll',
      parentCategory: 'expenses',
      type: 'expense',
      accountId: entry.accountId,
      budgetId: entry.budgetId,
    };

    const docRef = await addDoc(collection(db, 'transactions'), transaction);
    return docRef.id;
  };

  const addPayrollEntry = useCallback(async (entry: Omit<PayrollEntry, 'id' | 'createdBy' | 'budgetId' | 'transactionId'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      if (!budgetState.currentBudget) throw new Error('No budget selected');

      const account = accountState.accounts.find(a => a.id === entry.accountId);
      if (!account) throw new Error('Account not found');

      const newEntry: Omit<PayrollEntry, 'id'> = {
        ...entry,
        budgetId: budgetState.currentBudget.id,
        createdBy: user.uid,
      };

      // If status is 'paid', create transaction and update account balance
      if (entry.status === 'paid') {
        await runTransaction(db, async (transaction) => {
          // Create transaction
          const transactionId = await createPayrollTransaction(newEntry);

          // Update account balance
          await updateAccountBalance(entry.accountId, entry.netPay, 'deduct');

          // Add payroll entry with transaction reference
          const payrollCollection = collection(db, 'payroll').withConverter(payrollConverter);
          const docRef = await addDoc(payrollCollection, {
            ...newEntry,
            transactionId,
          });

          dispatch({
            type: 'ADD_PAYROLL_ENTRY',
            payload: { ...newEntry, id: docRef.id, transactionId },
          });
        });
      } else {
        // If not paid, just create the payroll entry
        const payrollCollection = collection(db, 'payroll').withConverter(payrollConverter);
        const docRef = await addDoc(payrollCollection, newEntry);
        dispatch({
          type: 'ADD_PAYROLL_ENTRY',
          payload: { ...newEntry, id: docRef.id },
        });
      }
    } catch (error) {
      console.error('Error adding payroll entry:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to add payroll entry' });
    }
  }, [budgetState.currentBudget, accountState.accounts, updateAccountBalance]);

  const updatePayrollEntry = useCallback(async (entry: PayrollEntry, originalEntry: PayrollEntry) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      if (!entry.id) throw new Error('Payroll entry ID is required for update');

      // Handle status changes that affect account balance
      if (originalEntry.status !== entry.status) {
        if (originalEntry.status === 'paid' && entry.status !== 'paid') {
          // Reverse previous payment
          if (originalEntry.transactionId) {
            await deleteDoc(doc(db, 'transactions', originalEntry.transactionId));
            await updateAccountBalance(originalEntry.accountId, originalEntry.netPay, 'add');
          }
        } else if (originalEntry.status !== 'paid' && entry.status === 'paid') {
          // Create new payment
          const transactionId = await createPayrollTransaction(entry);
          entry.transactionId = transactionId;
          await updateAccountBalance(entry.accountId, entry.netPay, 'deduct');
        }
      } else if (entry.status === 'paid' && entry.netPay !== originalEntry.netPay) {
        // Handle amount changes for paid entries
        const difference = entry.netPay - originalEntry.netPay;
        if (difference > 0) {
          await updateAccountBalance(entry.accountId, Math.abs(difference), 'deduct');
        } else {
          await updateAccountBalance(entry.accountId, Math.abs(difference), 'add');
        }
        
        // Update associated transaction if it exists
        if (entry.transactionId) {
          const transactionRef = doc(db, 'transactions', entry.transactionId);
          await updateDoc(transactionRef, {
            amount: entry.netPay,
            description: `Payroll payment for ${entry.employeeName}`,
          });
        }
      }

      const { id, ...updateData } = entry;
      const payrollRef = doc(db, 'payroll', id).withConverter(payrollConverter);
      await updateDoc(payrollRef, updateData);

      dispatch({ type: 'UPDATE_PAYROLL_ENTRY', payload: entry });
    } catch (error) {
      console.error('Error updating payroll entry:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update payroll entry' });
    }
  }, [updateAccountBalance]);

  const deletePayrollEntry = useCallback(async (entryId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const entry = state.payrollEntries.find(e => e.id === entryId);
      if (!entry) throw new Error('Payroll entry not found');

      // If entry was paid, reverse the payment
      if (entry.status === 'paid' && entry.transactionId) {
        await deleteDoc(doc(db, 'transactions', entry.transactionId));
        await updateAccountBalance(entry.accountId, entry.netPay, 'add');
      }

      await deleteDoc(doc(db, 'payroll', entryId));
      dispatch({ type: 'DELETE_PAYROLL_ENTRY', payload: entryId });
    } catch (error) {
      console.error('Error deleting payroll entry:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete payroll entry' });
    }
  }, [state.payrollEntries, updateAccountBalance]);

  const loadPayrollEntries = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      if (!budgetState.currentBudget) throw new Error('No budget selected');

      const q = query(
        collection(db, 'payroll'),
        where('createdBy', '==', user.uid),
        where('budgetId', '==', budgetState.currentBudget.id),
        orderBy('paymentDate', 'desc')
      ).withConverter(payrollConverter);

      const querySnapshot = await getDocs(q);
      const payrollEntries = querySnapshot.docs.map(doc => doc.data());

      const totalPayroll = payrollEntries.reduce((sum, entry) => sum + entry.netPay, 0);

      const monthlyPayroll = payrollEntries.reduce((acc, entry) => {
        const monthYear = entry.paymentDate.toISOString().slice(0, 7);
        acc[monthYear] = (acc[monthYear] || 0) + entry.netPay;
        return acc;
      }, {} as Record<string, number>);

      dispatch({
        type: 'LOAD_PAYROLL_ENTRIES',
        payload: {
          payrollEntries,
          totalPayroll,
          monthlyPayroll,
        },
      });
    } catch (error) {
      console.error('Error loading payroll entries:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load payroll entries' });
    }
  }, [budgetState.currentBudget]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && budgetState.currentBudget) {
        loadPayrollEntries();
      } else {
        dispatch({ type: 'LOAD_PAYROLL_ENTRIES', payload: initialState });
      }
    });

    return () => unsubscribe();
  }, [loadPayrollEntries, budgetState.currentBudget]);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  return (
    <PayrollContext.Provider
      value={{
        state,
        addPayrollEntry,
        loadPayrollEntries,
        updatePayrollEntry,
        deletePayrollEntry,
        clearError,
      }}
    >
      {children}
    </PayrollContext.Provider>
  );
}

export function usePayroll(): PayrollContextType {
  const context = useContext(PayrollContext);
  if (!context) {
    throw new Error('usePayroll must be used within a PayrollProvider');
  }
  return context;
}